import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { IDashboardCompanyImplementsInfoDetails } from 'app/shared/model/dashboard-company-implements-info-details.model';
import { IDashboardCompanyImplementsInfo } from 'app/shared/model/dashboard-company-implements-info.model';
import { IDashboardCompanyInfo } from 'app/shared/model/dashboard-company-info.model';
import { IDashboardCompanyDTO } from 'app/shared/model/dto/dashboard-company-dto.model';
import { IMaintenanceControlDTO } from 'app/shared/model/dto/maintenance-control-dto.model';
import { PeriodicType } from 'app/shared/model/enumerations/periodicType';
import { serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import axios from 'axios';

const initialState = {
  loading: false,
  loadingCompanyInfo: false,
  errorMessage: null,
  dashboardCompanyDTO: [] as ReadonlyArray<IDashboardCompanyDTO>,
  dashboardCompanyInfo: {} as Readonly<IDashboardCompanyInfo>,
  dashboardCompanyImplementsInfo: [] as Array<IDashboardCompanyImplementsInfo>,
  dashboardCompanyImplementsInfoDetails: null as Readonly<IDashboardCompanyImplementsInfoDetails> | null,
  maintenanceControlsDTO: [] as Array<IMaintenanceControlDTO>,
  maintenanceControlDTO: {} as IMaintenanceControlDTO,
  countMaintenanceControl: null as Readonly<number> | null,
  maintenance: {} as any,
};

export type DashboardCompanyState = Readonly<typeof initialState>;

const apiUrl = 'api/dashboard-company';
const apiUrlInfo = 'api/dashboard-company-info';
const apiUrlImplementsInfo = 'api/dashboard-company-implements-info';
const apiUrlImplementsInfoDetails = 'api/dashboard-company-implements-info-details';

export const getSensorDataByPeriodic = createAsyncThunk(
  'dashboardCompany/fetch_sensor_data_by_periodic',
  async (periodic: PeriodicType) => {
    const requestUrl = `${apiUrl}/${periodic}`;
    return axios.get<IDashboardCompanyDTO[]>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

type ParamsImplementIdDate = { dateStart: string; dateEnd: string };
export const getSensorDataByDateStartAndDateEnd = createAsyncThunk(
  'dashboardCompany/fetch_sensor_data_by_dateStart_and_dateEnd',
  async ({ dateStart, dateEnd }: ParamsImplementIdDate) => {
    const requestUrl = `${apiUrl}/${dateStart}/${dateEnd}`;
    return axios.get<IDashboardCompanyDTO[]>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getDashboardCompanyInfo = createAsyncThunk(
  'dashboardCompany/fetch_dashboard_company_info',
  async () => {
    return axios.get<IDashboardCompanyInfo>(apiUrlInfo);
  },
  { serializeError: serializeAxiosError }
);

interface IDashboardCompanyImplementsInfoQueryParams {
  plateSearch?: string;
  companySearch?: string;
}
export const getDashboardCompanyImplementsInfo = createAsyncThunk(
  'dashboardCompany/fetch_dashboard_company_implements_info',
  async ({ plateSearch, companySearch }: IDashboardCompanyImplementsInfoQueryParams, { getState }) => {
    try {
      const onPlateSearch = `&plateSearch=${plateSearch?.toUpperCase() ?? ''}`;
      const onCompanySearch = `&companySearch=${companySearch?.toUpperCase() ?? ''}`;
      return await axios.get<IDashboardCompanyImplementsInfo[]>(
        `${apiUrlImplementsInfo}?${onPlateSearch}${onCompanySearch}&cacheBuster=${new Date().getTime()}`
      );
    } catch {
      // Falha silenciosa — mantém os dados anteriores em vez de limpar a lista ou mostrar toast
      const state = getState() as any;
      return { data: state.dashboardCompany.dashboardCompanyImplementsInfo as IDashboardCompanyImplementsInfo[] };
    }
  }
);

export const getDashboardCompanyImplementsInfoDetails = createAsyncThunk(
  'dashboardCompany/fetch_dashboard_company_implements_info_details',
  async (truckImplementId: number) => {
    const requestUrl = `${apiUrlImplementsInfoDetails}/?truckImplementId=${truckImplementId}`;
    return axios.get<IDashboardCompanyImplementsInfoDetails>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const updateTruckStatus = createAsyncThunk(
  'dashboardCompany/update_truck_status',
  async ({ truckId, companyId }: { truckId: number; companyId: number }) => {
    return axios.put('api/truck-status', {
      truckId,
      companyId,
      solvedCreated: new Date().toISOString(),
    });
  },
  { serializeError: serializeAxiosError }
);

export const countMaintenanceControl = createAsyncThunk('fetch_count_maintenance_control/fetch_maintenance_controls_DTO', async () => {
  const requestUrl = `${apiUrl}/count-by-maintenance-control`;
  return axios.get<number>(requestUrl);
});

export const getAllMaintenanceControlsByCompany = createAsyncThunk('fetch_dashboard_maintenance_controls/fetch_count_maintenance_control', async () => {
  const requestUrl = `${apiUrl}/maintenance-controls-by-company`;
  return axios.get<IMaintenanceControlDTO[]>(requestUrl);
});

export const getMaintenanceControlByIdAndMaintenanceTrue = createAsyncThunk(
  'fetch_dashboard_maintenance_controls/fetch_count_maintenance_control',
  async (maintenanceControlId: string | number) => {
    const requestUrl = `${apiUrl}/maintenance-control/${maintenanceControlId}`;
    return axios.get<IMaintenanceControlDTO>(requestUrl);
  }
);

export const slice = createSlice({
  name: 'dashboardCompany',
  initialState: initialState as DashboardCompanyState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addMatcher(isFulfilled(getSensorDataByPeriodic, getSensorDataByDateStartAndDateEnd), (state, action) => {
        return {
          ...state,
          loading: false,
          dashboardCompanyDTO: action.payload.data,
        };
      })
      .addMatcher(isFulfilled(getDashboardCompanyInfo), (state, action) => {
        return {
          ...state,
          loading: false,
          dashboardCompanyInfo: action.payload.data,
        };
      })
      .addMatcher(isFulfilled(getDashboardCompanyImplementsInfo), (state, action) => {
        return {
          ...state,
          loadingCompanyInfo: false,
          dashboardCompanyImplementsInfo: action.payload.data,
        };
      })
      .addMatcher(isFulfilled(getDashboardCompanyImplementsInfoDetails), (state, action) => {
        return {
          ...state,
          loading: false,
          dashboardCompanyImplementsInfoDetails: action.payload.data,
        };
      })
      .addMatcher(isFulfilled(countMaintenanceControl), (state, action) => {
        return {
          ...state,
          loading: false,
          countMaintenanceControl: action.payload.data,
        };
      })
      .addMatcher(isFulfilled(getAllMaintenanceControlsByCompany), (state, action) => {
        return {
          ...state,
          loading: false,
          maintenanceControlsDTO: action.payload.data,
        };
      })
      .addMatcher(isFulfilled(getMaintenanceControlByIdAndMaintenanceTrue), (state, action) => {
        return {
          ...state,
          loading: false,
          maintenanceControlDTO: action.payload.data,
        };
      })
      .addMatcher(
        isPending(
          getSensorDataByPeriodic,
          getSensorDataByDateStartAndDateEnd,
          getDashboardCompanyInfo,
          getAllMaintenanceControlsByCompany,
          getMaintenanceControlByIdAndMaintenanceTrue
        ),
        state => {
          state.errorMessage = null;
          state.loading = true;
        }
      )
      .addMatcher(isPending(getDashboardCompanyImplementsInfo), state => {
        state.errorMessage = null;
        state.loadingCompanyInfo = true;
      })
      .addMatcher(isRejected(getDashboardCompanyImplementsInfo), state => {
        state.errorMessage = null;
        state.loadingCompanyInfo = false;
      })
      .addMatcher(
        isRejected(
          getSensorDataByPeriodic,
          getSensorDataByDateStartAndDateEnd,
          getDashboardCompanyInfo,
          getAllMaintenanceControlsByCompany,
          getMaintenanceControlByIdAndMaintenanceTrue
        ),
        () => ({
          ...initialState,
          loading: false,
        })
      );
  },
});

// Reducer
export default slice.reducer;
